import { autoPublishChannels, AutoPublishKey, reservationKind } from '../shared/autoPublish';
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Card } from '../shared/components/Card';
import { PageHeader } from '../shared/components/PageHeader';
import { Button } from '../shared/components/Button';
import { Alert, Spinner } from '../shared/components/feedback';
import { Input } from '../shared/components/Field';
import { Avatar } from '../shared/components/Avatar';
import {
  CalendarIcon,
  ChevronDownIcon,
  CommentIcon,
  EditIcon,
  PlusIcon,
  RefreshIcon,
  SearchIcon,
  TagIcon,
  TrashIcon,
} from '../shared/components/icons';
import { useActiveAccount } from '../account/ActiveAccountContext';
import { useDialog } from '../shared/dialog/DialogProvider';
import * as threadPostApi from '../api/threadPost';
import * as threadReservationApi from '../api/threadReservation';
import * as threadScheduleApi from '../api/threadSchedule';
import * as threadCommentReplyApi from '../api/threadCommentReply';
import type { ThreadCommentReply } from '../api/threadCommentReply';
import { ApiError } from '../api/client';
import type {
  PublishMode,
  ThreadAccountPostReservation,
  ThreadAccountPostResponse,
  ThreadAccountSchedule,
} from '../api/types';
import * as productApi from '../api/product';
import type { Product } from '../api/product';
import { ReservationDraftModal } from './board/ReservationDraftModal';
import { ReservationCalendar } from './board/ReservationCalendar';
import { NotionImportModal } from './board/NotionImportModal';
import { ReservationReconcileForm } from './board/ReservationReconcileForm';
import { activeReservationStatuses, editableReservation, isLegacyReservation, reservationStatusLabel } from './board/reservationState';
import { Switch } from '../shared/components/Switch';
import { Modal } from '../shared/components/Modal';
import {
  formatWallClockDateKo,
  formatWallClockKo,
  pickWallClock,
  wallClockSortKey,
} from '../shared/datetimeWallClock';

type SortOrder = 'newest' | 'oldest';

const SORT_LABEL: Record<SortOrder, string> = {
  newest: '최신순',
  oldest: '오래된순',
};

const BOARD_PAGE_SIZE = 5;

function normalizeSearch(value: string) {
  return value.trim().toLowerCase();
}

/** 서버 벽시계 문자열 표시 — Date 변환 금지 (타임존만큼 밀림) */
function formatLocalDateTime(value?: string | null): string {
  return formatWallClockKo(value);
}

function matchesSearch(haystack: string | null | undefined, query: string) {
  if (!query) return true;
  return (haystack ?? '').toLowerCase().includes(query);
}

function formatInsightCount(value?: number | null): string {
  if (value == null) return '-';
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 10_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toLocaleString('ko-KR');
}

export default function BoardPage() {
  const { selectedAccountId } = useActiveAccount();
  return <AccountBoard key={selectedAccountId ?? 'none'} />;
}

function AccountBoard() {
  const [showNotionImport, setShowNotionImport] = useState(false);
  const { selectedAccount, selectedAccountId, fetchAccounts } = useActiveAccount();
  const { confirm } = useDialog();

  const [tasks, setTasks] = useState<ThreadAccountPostReservation[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preparingId, setPreparingId] = useState<number | null>(null);
  const [draftModalTask, setDraftModalTask] =
    useState<ThreadAccountPostReservation | null>(null);
  const [selectedPost, setSelectedPost] =
    useState<ThreadAccountPostResponse | null>(null);

  const [donePosts, setDonePosts] = useState<ThreadAccountPostResponse[]>([]);
  const [doneLoading, setDoneLoading] = useState(false);
  const [doneError, setDoneError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [copyingId, setCopyingId] = useState<number | null>(null);

  const [subject, setSubject] = useState('');
  const [, setPublishMode] = useState<PublishMode>('PERSONA');
  const [productId, setProductId] = useState<number | ''>('');
  const [products, setProducts] = useState<Product[]>([]);
  const [composing, setComposing] = useState(false);
  const [clearingFailed, setClearingFailed] = useState(false);

  const [sortOrder, setSortOrder] = useState<SortOrder>('newest');
  const [registeredPage, setRegisteredPage] = useState(1);
  const [deletedPage, setDeletedPage] = useState(1);
  const [tasksPage, setTasksPage] = useState(1);
  const [tasksQuery, setTasksQuery] = useState('');
  const [registeredQuery, setRegisteredQuery] = useState('');
  const [deletedQuery, setDeletedQuery] = useState('');
  const [commentReplies, setCommentReplies] = useState<ThreadCommentReply[]>(
    []
  );
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsQuery, setCommentsQuery] = useState('');
  const [commentsPage, setCommentsPage] = useState(1);
  const [autoReplySaving, setAutoReplySaving] = useState(false);
  const [schedule, setSchedule] = useState<ThreadAccountSchedule | null>(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const activeAccountIdRef = useRef(selectedAccountId);
  activeAccountIdRef.current = selectedAccountId;
  const scheduleRequestRef = useRef(0);
  const scheduleMutationRef = useRef(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const postsRequestIdRef = useRef(0);
  const [showExtras, setShowExtras] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showAutomation, setShowAutomation] = useState(false);
  const [archivedTasks, setArchivedTasks] = useState<ThreadAccountPostReservation[]>([]);
  const [legacyTasks, setLegacyTasks] = useState<ThreadAccountPostReservation[]>([]);
  const [pollCycle, setPollCycle] = useState(0);
  const [pollingStopped, setPollingStopped] = useState(false);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const completedSignature = useRef<string | null>(null);
  const reservationRequest = useRef(0);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { scheduleMutationRef.current = false; setScheduleSaving(false); setSchedule(null); }, [selectedAccountId]);

  const fetchSchedule = useCallback(async (silent = false) => {
    if (selectedAccountId == null || scheduleMutationRef.current) return;
    const request = ++scheduleRequestRef.current;
    if (!silent) setScheduleLoading(true);
    try {
      const data = await threadScheduleApi.getSchedule(selectedAccountId);
      if (mounted.current && activeAccountIdRef.current === selectedAccountId && request === scheduleRequestRef.current) setSchedule(data);
    } catch {
      if (!silent && mounted.current && activeAccountIdRef.current === selectedAccountId && request === scheduleRequestRef.current) setSchedule(null);
    } finally {
      if (mounted.current && activeAccountIdRef.current === selectedAccountId) setScheduleLoading(false);
    }
  }, [selectedAccountId]);

  useEffect(() => {
    if (selectedAccountId == null) {
      setProducts([]);
      return;
    }
    productApi
      .listProducts()
      .then((list) => setProducts(list.filter((p) => p.status === 'ACTIVE')))
      .catch(() => setProducts([]));
  }, [selectedAccountId]);

  const fetchReservations = useCallback(async (silent = false) => {
    if (selectedAccountId == null) return false;
    const request = ++reservationRequest.current;
    if (!silent) setTasksLoading(true);
    try {
      const data = await threadReservationApi.listReservations(selectedAccountId);
      if (!mounted.current || request !== reservationRequest.current) return false;
      const normalized = data.map((r) => ({ ...r, scheduledAt: pickWallClock(r) }));
      setTasks(normalized.filter((r) => activeReservationStatuses.includes(r.status) && !isLegacyReservation(r)));
      setArchivedTasks(normalized.filter((r) => r.status === 'CANCELLED' && r.errorMessage && !isLegacyReservation(r)));
      setLegacyTasks(normalized.filter(isLegacyReservation));
      setTasksError(null);
      const signature = data.filter((r) => r.status === 'DONE').map((r) => r.id).sort().join(',');
      const changed = completedSignature.current != null && signature !== completedSignature.current;
      completedSignature.current = signature;
      return changed;
    } catch (err) {
      if (mounted.current && request === reservationRequest.current) {
        setTasksError(err instanceof ApiError ? err.message : '예약 상태를 갱신하지 못했습니다. 새로고침해 주세요.');
      }
      return false;
    } finally {
      if (mounted.current && request === reservationRequest.current) setTasksLoading(false);
    }
  }, [selectedAccountId]);

  const fetchPosts = useCallback(async (includeInsights = true, silent = false) => {
    if (selectedAccountId == null) return;
    const accountId = selectedAccountId;
    const requestId = ++postsRequestIdRef.current;
    if (!silent) setDoneLoading(true);
    setDoneError(null);
    try {
      const data = await threadPostApi.getPostsByThreadAccount(accountId);
      if (!mounted.current || requestId !== postsRequestIdRef.current) return;
      setDonePosts((previous) => {
        const insights = new Map(previous.map((post) => [post.id, post.insights]));
        return data.map((post) => ({ ...post, insights: post.insights ?? insights.get(post.id) }));
      });
      setDoneLoading(false);

      if (!includeInsights) return;
      void threadPostApi
        .getPostsByThreadAccount(accountId, { includeInsights: true })
        .then((withInsights) => {
          if (!mounted.current || requestId !== postsRequestIdRef.current) return;
          setDonePosts(withInsights);
        })
        .catch(() => undefined);
    } catch (err) {
      if (!mounted.current || requestId !== postsRequestIdRef.current) return;
      setDoneError(
        err instanceof ApiError ? err.message : '목록을 불러오지 못했습니다.'
      );
      setDoneLoading(false);
    }
  }, [selectedAccountId]);

  const fetchCommentReplies = useCallback(async (silent = false) => {
    if (selectedAccountId == null) return;
    if (!silent) setCommentsLoading(true);
    try {
      const data = await threadCommentReplyApi.listCommentReplies(
        selectedAccountId
      );
      if (!mounted.current) return;
      setCommentReplies(data);
      setCommentsError(null);
    } catch (err) {
      if (mounted.current) setCommentsError(err instanceof Error ? err.message : '댓글을 불러오지 못했습니다.');
    } finally {
      if (mounted.current) setCommentsLoading(false);
    }
  }, [selectedAccountId]);

  useEffect(() => {
    fetchPosts();
    fetchReservations();
    fetchSchedule();
    fetchCommentReplies();
  }, [fetchPosts, fetchReservations, fetchSchedule, fetchCommentReplies]);

  const needsStatusRefresh = !!schedule?.enabled || tasks.some((task) => task.status === 'PENDING' || task.status === 'PROCESSING' || task.status === 'PUBLISHING');
  useEffect(() => {
    if (!needsStatusRefresh) return;
    let cancelled = false;
    let inFlight = false;
    let ticks = 0;
    setPollingStopped(false);
    const timer = window.setInterval(async () => {
      if (document.visibilityState === 'hidden' || inFlight || cancelled) return;
      if (++ticks > 40) {
        window.clearInterval(timer);
        setPollingStopped(true);
        return;
      }
      inFlight = true;
      try {
        const changed = await fetchReservations(true);
        if (cancelled) return;
        if (changed) await fetchPosts(false, true);
        if (ticks % 4 === 0) {
          await Promise.allSettled([fetchSchedule(true), fetchCommentReplies(true)]);
        }
      } finally { inFlight = false; }
    }, 15_000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [needsStatusRefresh, pollCycle, fetchReservations, fetchPosts, fetchSchedule, fetchCommentReplies]);

  const handleAutoPublishToggle = useCallback(
    async (key: AutoPublishKey, next: boolean) => {
      if (selectedAccountId == null) return;
      const accountId = selectedAccountId;
      scheduleMutationRef.current = true;
      scheduleRequestRef.current++;
      setScheduleSaving(true);
      try {
        const data = await threadScheduleApi.upsertSchedule(accountId, { [key]: next });
        if (!mounted.current || activeAccountIdRef.current !== accountId) return;
        setSchedule(data);
        await fetchReservations();
        setPollCycle((cycle) => cycle + 1);
      } catch (err) {
        if (!mounted.current || activeAccountIdRef.current !== accountId) return;
        await confirm({ title: '설정 실패', message: err instanceof ApiError ? err.description || err.message
          : '자동 발송 설정을 바꾸지 못했습니다.', tone: 'danger', confirmText: '확인' });
      } finally {
        if (mounted.current && activeAccountIdRef.current === accountId) {
          scheduleMutationRef.current = false;
          setScheduleSaving(false);
        }
      }
    }, [selectedAccountId, confirm, fetchReservations]
  );

  const handleAutoReplyToggle = useCallback(
    async (next: boolean) => {
      if (selectedAccountId == null) return;
      setAutoReplySaving(true);
      const prev = schedule?.autoReplyEnabled ?? false;
      setSchedule((s) => (s ? { ...s, autoReplyEnabled: next } : s));
      try {
        const data = await threadScheduleApi.upsertSchedule(selectedAccountId, {
          autoReplyEnabled: next,
        });
        setSchedule(data);
        if (next) await fetchCommentReplies();
      } catch (err) {
        setSchedule((s) => (s ? { ...s, autoReplyEnabled: prev } : s));
        await confirm({
          title: '설정 실패',
          message:
            err instanceof ApiError
              ? err.message
              : '댓글 자동 대응 설정을 바꾸지 못했습니다. 계정 재연동(답글·삭제 권한)이 필요할 수 있습니다.',
          tone: 'danger',
          confirmText: '확인',
        });
      } finally {
        setAutoReplySaving(false);
      }
    },
    [
      selectedAccountId,
      schedule?.autoReplyEnabled,
      confirm,
      fetchCommentReplies,
    ]
  );

  const sortPosts = useCallback(
    (list: ThreadAccountPostResponse[]) => {
      const next = [...list];
      next.sort((a, b) => {
        const da = a.registeredAt
          ? wallClockSortKey(a.registeredAt)
          : a.unRegisteredAt
            ? wallClockSortKey(a.unRegisteredAt)
            : a.id;
        const db = b.registeredAt
          ? wallClockSortKey(b.registeredAt)
          : b.unRegisteredAt
            ? wallClockSortKey(b.unRegisteredAt)
            : b.id;
        return sortOrder === 'newest' ? db - da : da - db;
      });
      return next;
    },
    [sortOrder]
  );

  const registeredPosts = useMemo(() => {
    const q = normalizeSearch(registeredQuery);
    return sortPosts(
      donePosts.filter((p) => {
        if (p.status !== 'REGISTERED') return false;
        if (!q) return true;
        return (
          matchesSearch(p.post, q) ||
          matchesSearch(p.category, q) ||
          matchesSearch(String(p.id), q)
        );
      })
    );
  }, [donePosts, sortPosts, registeredQuery]);

  const deletedPosts = useMemo(() => {
    const q = normalizeSearch(deletedQuery);
    return sortPosts(
      donePosts.filter((p) => {
        if (p.status !== 'UNREGISTERED') return false;
        if (!q) return true;
        return (
          matchesSearch(p.post, q) ||
          matchesSearch(p.category, q) ||
          matchesSearch(String(p.id), q)
        );
      })
    );
  }, [donePosts, sortPosts, deletedQuery]);

  const registeredTotalPages = Math.max(
    1,
    Math.ceil(registeredPosts.length / BOARD_PAGE_SIZE)
  );
  const deletedTotalPages = Math.max(
    1,
    Math.ceil(deletedPosts.length / BOARD_PAGE_SIZE)
  );

  const pagedRegisteredPosts = useMemo(() => {
    const page = Math.min(registeredPage, registeredTotalPages);
    const start = (page - 1) * BOARD_PAGE_SIZE;
    return registeredPosts.slice(start, start + BOARD_PAGE_SIZE);
  }, [registeredPosts, registeredPage, registeredTotalPages]);

  const pagedDeletedPosts = useMemo(() => {
    const page = Math.min(deletedPage, deletedTotalPages);
    const start = (page - 1) * BOARD_PAGE_SIZE;
    return deletedPosts.slice(start, start + BOARD_PAGE_SIZE);
  }, [deletedPosts, deletedPage, deletedTotalPages]);

  useEffect(() => {
    setRegisteredPage(1);
    setDeletedPage(1);
    setTasksPage(1);
    setTasksQuery('');
    setRegisteredQuery('');
    setDeletedQuery('');
    setCommentsQuery('');
    setCommentsPage(1);
  }, [selectedAccountId]);

  useEffect(() => {
    setRegisteredPage(1);
    setDeletedPage(1);
  }, [sortOrder]);

  useEffect(() => {
    setTasksPage(1);
  }, [tasksQuery]);

  useEffect(() => {
    setRegisteredPage(1);
  }, [registeredQuery]);

  useEffect(() => {
    setDeletedPage(1);
  }, [deletedQuery]);

  useEffect(() => {
    setCommentsPage(1);
  }, [commentsQuery]);

  useEffect(() => {
    if (registeredPage > registeredTotalPages) {
      setRegisteredPage(registeredTotalPages);
    }
  }, [registeredPage, registeredTotalPages]);

  useEffect(() => {
    if (deletedPage > deletedTotalPages) {
      setDeletedPage(deletedTotalPages);
    }
  }, [deletedPage, deletedTotalPages]);

  const visibleTasks = useMemo(() => {
    const q = normalizeSearch(tasksQuery);
    const list = tasks.filter((task) => {
      if (!q) return true;
      return (
        matchesSearch(task.subject, q) ||
        matchesSearch(task.draftPost, q) ||
        matchesSearch(task.errorMessage, q) ||
        matchesSearch(reservationStatusLabel(task.status), q) ||
        matchesSearch(String(task.id), q)
      );
    });
    list.sort((a, b) => {
      const da = a.scheduledAt
        ? wallClockSortKey(a.scheduledAt) || a.id
        : a.createdAt
          ? wallClockSortKey(a.createdAt) || a.id
          : a.id;
      const db = b.scheduledAt
        ? wallClockSortKey(b.scheduledAt) || b.id
        : b.createdAt
          ? wallClockSortKey(b.createdAt) || b.id
          : b.id;
      return da - db;
    });
    return list;
  }, [tasks, tasksQuery]);

  const failedTaskCount = useMemo(
    () => tasks.filter((task) => task.status === 'FAILED').length,
    [tasks]
  );
  const stalledTaskCount = tasks.filter((task) =>
    (task.status === 'PROCESSING' || task.status === 'PUBLISHING') && task.startedAt &&
    Date.now() + 9 * 3600000 - wallClockSortKey(task.startedAt) > 15 * 60000
  ).length;
  const unknownTaskCount = tasks.filter((task) => task.status === 'PUBLISH_UNKNOWN').length;
  const awaitingCount = tasks.filter((task) => task.status === 'AWAITING_APPROVAL').length;

  const tasksTotalPages = Math.max(
    1,
    Math.ceil(visibleTasks.length / BOARD_PAGE_SIZE)
  );

  const pagedTasks = useMemo(() => {
    const page = Math.min(tasksPage, tasksTotalPages);
    const start = (page - 1) * BOARD_PAGE_SIZE;
    return visibleTasks.slice(start, start + BOARD_PAGE_SIZE);
  }, [visibleTasks, tasksPage, tasksTotalPages]);

  useEffect(() => {
    if (tasksPage > tasksTotalPages) {
      setTasksPage(tasksTotalPages);
    }
  }, [tasksPage, tasksTotalPages]);

  const visibleComments = useMemo(() => {
    const q = normalizeSearch(commentsQuery);
    return commentReplies.filter((item) => {
      if (!q) return true;
      return (
        matchesSearch(item.commentText, q) ||
        matchesSearch(item.replyText, q) ||
        matchesSearch(item.commentUsername, q) ||
        matchesSearch(item.decisionReason, q) ||
        matchesSearch(item.status, q)
      );
    });
  }, [commentReplies, commentsQuery]);

  const commentsTotalPages = Math.max(
    1,
    Math.ceil(visibleComments.length / BOARD_PAGE_SIZE)
  );

  const pagedComments = useMemo(() => {
    const page = Math.min(commentsPage, commentsTotalPages);
    const start = (page - 1) * BOARD_PAGE_SIZE;
    return visibleComments.slice(start, start + BOARD_PAGE_SIZE);
  }, [visibleComments, commentsPage, commentsTotalPages]);

  useEffect(() => {
    if (commentsPage > commentsTotalPages) {
      setCommentsPage(commentsTotalPages);
    }
  }, [commentsPage, commentsTotalPages]);

  const cycleSort = () => {
    setSortOrder((prev) => (prev === 'newest' ? 'oldest' : 'newest'));
  };

  const handlePrepare = useCallback(
    async (task: ThreadAccountPostReservation) => {
      setBusy(true);
      setPreparingId(task.id);
      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id ? { ...t, status: 'PROCESSING' as const } : t
        )
      );
      try {
        const ready = await threadReservationApi.prepareReservation(task.id);
        setTasks((prev) =>
          prev.map((t) => (t.id === ready.id ? ready : t))
        );
        setDraftModalTask(ready);
      } catch (err) {
        const msg =
          err instanceof ApiError ? err.message : '컨텐츠 제작에 실패했습니다.';
        await fetchReservations();
        await confirm({
          title: '제작 실패',
          message: msg,
          tone: 'danger',
          confirmText: '확인',
        });
      } finally {
        setPreparingId(null);
        setBusy(false);
      }
    },
    [confirm, fetchReservations]
  );

  const handleRetryFailed = useCallback(
    async (task: ThreadAccountPostReservation) => {
      if (task.status !== 'FAILED') {
        void handlePrepare(task);
        return;
      }
      setBusy(true);
      try {
        const restored = await threadReservationApi.retryReservation(task.id);
        setTasks((prev) =>
          prev.map((t) => (t.id === task.id ? restored : t))
        );
        if (editableReservation(restored)) setDraftModalTask(restored);
        else if (restored.status === 'PENDING') await handlePrepare(restored);
      } catch (err) {
        await confirm({
          title: '재시도 실패',
          message:
            err instanceof ApiError
              ? err.message
              : '재시도에 실패했습니다. 다시 시도해 주세요.',
          tone: 'danger',
          confirmText: '확인',
        });
      } finally { setBusy(false); }
    },
    [handlePrepare, confirm]
  );

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault();
    if (selectedAccountId == null || !subject.trim()) return;
    setBusy(true);
    try {
      const mode: PublishMode =
        productId !== '' ? 'PRODUCT' : 'PERSONA';
      const created = await threadReservationApi.createReservation(
        selectedAccountId,
        {
          subject: subject.trim(),
          publishMode: mode,
          productId: mode === 'PRODUCT' ? Number(productId) : null,
        }
      );
      setTasks((prev) => [created, ...prev]);
      setSubject('');
      setPublishMode('PERSONA');
      setProductId('');
      setComposing(false);
      if (created.status === 'READY') {
        setDraftModalTask(created);
      }
    } catch (err) {
      await confirm({
        title: '예약 추가 실패',
        message:
          err instanceof ApiError
            ? err.message
            : '예약 추가에 실패했습니다. 초안 제작까지 시간이 걸릴 수 있습니다.',
        tone: 'danger',
        confirmText: '확인',
      });
      await fetchReservations();
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveTask = async (task: ThreadAccountPostReservation) => {
    const accountId = selectedAccountId;
    const ok = await confirm({
      title: '예약 삭제',
      message: task.status === 'PUBLISH_UNKNOWN'
        ? `"${task.subject}" 예약을 발행 결과 확인 없이 삭제할까요?\n예약 목록에서 제외하며, Threads에 이미 게시된 글은 삭제하지 않습니다.`
        : `"${task.subject}" 예약을 삭제할까요?`,
      confirmText: '삭제',
      cancelText: '취소',
      tone: 'danger',
    });
    if (!ok || !mounted.current || accountId !== activeAccountIdRef.current) return;
    try {
      await threadReservationApi.deleteReservation(task.id);
      if (!mounted.current || accountId !== activeAccountIdRef.current) return;
      // Discard any list response that started before this deletion completed.
      ++reservationRequest.current;
      setTasksLoading(false);
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
      setDraftModalTask((current) =>
        current?.id === task.id ? null : current
      );
    } catch (err) {
      if (!mounted.current || accountId !== activeAccountIdRef.current) return;
      await fetchReservations();
      await confirm({
        title: '예약 삭제 실패',
        message:
          err instanceof ApiError
            ? err.message
            : '예약을 삭제하지 못했습니다. 잠시 후 다시 시도해 주세요.',
        tone: 'danger',
        confirmText: '확인',
      });
    }
  };

  const handleRemoveFailedTasks = async () => {
    if (selectedAccountId == null || failedTaskCount === 0) return;
    const ok = await confirm({
      title: '실패 기록 보관',
      message: `실패 ${failedTaskCount}건을 보관함으로 옮길까요? 오류 이력은 보존됩니다.`,
      confirmText: '보관',
      cancelText: '취소',
      tone: 'danger',
    });
    if (!ok) return;
    setClearingFailed(true);
    try {
      await threadReservationApi.deleteFailedReservations(selectedAccountId);
      await fetchReservations();
      setDraftModalTask((current) =>
        current?.status === 'FAILED' ? null : current
      );
    } catch (err) {
      await fetchReservations();
      await confirm({
        title: '삭제 실패',
        message:
          err instanceof ApiError
            ? err.message
            : '실패 기록을 보관하지 못했습니다. 잠시 후 다시 시도해 주세요.',
        tone: 'danger',
        confirmText: '확인',
      });
    } finally {
      setClearingFailed(false);
    }
  };

  const handleDelete = async (
    post: ThreadAccountPostResponse,
    event?: MouseEvent
  ) => {
    event?.stopPropagation();
    const ok = await confirm({
      title: '게시물 삭제',
      message: `게시물 #${post.id}을(를) 삭제할까요?\nThreads에서는 삭제되고, 보드의 「삭제된 글」로 이동합니다.`,
      confirmText: '삭제',
      cancelText: '취소',
      tone: 'danger',
    });
    if (!ok) return;
    setDeletingId(post.id);
    try {
      await threadPostApi.deletePost(post.id);
      setDonePosts((prev) =>
        prev.map((p) =>
          p.id === post.id
            ? {
                ...p,
                status: 'UNREGISTERED',
                unRegisteredAt: new Date().toLocaleString('sv-SE').replace(' ', 'T'),
                threadPostUrl: undefined,
              }
            : p
        )
      );
    } catch {
      await fetchPosts();
    } finally {
      setDeletingId(null);
    }
  };

  const handleCopyPost = async (
    post: ThreadAccountPostResponse,
    event?: MouseEvent
  ) => {
    event?.stopPropagation();
    setCopyingId(post.id);
    try {
      await navigator.clipboard.writeText(post.post);
      await confirm({ title: '본문 복사 완료', message: '삭제된 글의 본문을 복사했습니다.', confirmText: '확인' });
    } catch {
      await confirm({ title: '본문 복사', message: <textarea readOnly value={post.post} aria-label="복사할 글 본문" style={{ width: '100%', minHeight: 160 }} onFocus={(event) => event.target.select()} />, confirmText: '닫기' });
    } finally { setCopyingId(null); }
  };

  const handleOpenThreadsPost = (post: ThreadAccountPostResponse) => {
    if (post.status !== 'REGISTERED' || !post.threadPostUrl) return;
    window.open(post.threadPostUrl, '_blank', 'noopener,noreferrer');
  };

  const postCommentReplies = useMemo(() => {
    if (!selectedPost) return [];
    return commentReplies.filter(
      (item) =>
        item.threadAccountPostId === selectedPost.id ||
        (!!selectedPost.threadPostId &&
          item.rootThreadPostId === selectedPost.threadPostId)
    );
  }, [commentReplies, selectedPost]);

  if (selectedAccountId == null || !selectedAccount) {
    return (
      <Page>
        <PageHeader title="콘텐츠 보드" description="초안 준비부터 예약 발행, 댓글 관리까지 한곳에서 확인하세요." />
        <Card>
        <NoAccount>
          <h2>작업할 계정을 선택해 주세요</h2>
          <p>
            오른쪽 상단 프로필 메뉴에서 Threads 계정을 선택하거나, 계정을
            연동하세요.
          </p>
          <AccountLink to="/account">계정 관리로 이동</AccountLink>
        </NoAccount>
        </Card>
      </Page>
    );
  }

  const profile = selectedAccount.threadAccountProfileResponse;
  const accountLabel = profile?.threadUserName
    ? `@${profile.threadUserName}`
    : `계정 #${selectedAccountId}`;

  return (
    <Page>
      <TopRow>
        <div>
          <PageEyebrow>콘텐츠 보드</PageEyebrow>
          <TitleWrap>
            <Title>{accountLabel}</Title>
            <SquareIcon to="/account" aria-label="계정 설정">
              <EditIcon size={17} />
            </SquareIcon>
          </TitleWrap>
          <PageDescription>초안 준비부터 예약 발행, 댓글 관리까지 한곳에서 확인하세요.</PageDescription>
        </div>

        <TopRight>
          <Avatar
            src={profile?.threadProfilePictureUrl}
            name={profile?.threadUserName}
            size={34}
          />
        </TopRight>
      </TopRow>

      <Overview aria-label="콘텐츠 현황">
        <OverviewItem><dt>예약 작업</dt><dd>{tasksLoading ? '—' : tasks.length}<span>건</span></dd></OverviewItem>
        <OverviewItem><dt>승인 대기</dt><dd>{tasksLoading ? '—' : awaitingCount}<span>건</span></dd></OverviewItem>
        <OverviewItem><dt>발행한 글</dt><dd>{doneLoading ? '—' : donePosts.filter((post) => post.status === 'REGISTERED').length}<span>건</span></dd></OverviewItem>
        <OverviewItem><dt>확인 필요</dt><dd>{tasksLoading ? '—' : failedTaskCount + unknownTaskCount + stalledTaskCount}<span>건</span></dd></OverviewItem>
      </Overview>

      <Toolbar>
        <AutomationToggle type="button" aria-expanded={showAutomation} aria-controls="board-automation-controls"
          onClick={() => setShowAutomation((expanded) => !expanded)}>
          <span>자동 발행·댓글 설정</span>
          <AutomationState>{scheduleLoading ? '확인 중' : schedule == null ? '상태 확인 필요' : `${autoPublishChannels.filter(({ key }) => schedule[key]).length}개 유형 켜짐`}</AutomationState>
          <ChevronDownIcon size={16} />
        </AutomationToggle>
        <ToolLeft id="board-automation-controls" $expanded={showAutomation}>
          {autoPublishChannels.map(({ key, label }) => <AutoPublishControl key={key}>
            <AutoPublishLabel>{label}<AutomationSuffix> 자동 발행</AutomationSuffix> {schedule?.[key] ? <AutoOn>ON</AutoOn> : <AutoOff>OFF</AutoOff>}</AutoPublishLabel>
            <Switch checked={!!schedule?.[key]} disabled={scheduleLoading || scheduleSaving || schedule == null}
              aria-label={label + ' 자동 발행'} onChange={(next) => void handleAutoPublishToggle(key, next)} />
          </AutoPublishControl>)}
          <ToolDivider />
          <AutoPublishControl>
            <AutoPublishLabel>
              AI 댓글
              {schedule?.autoReplyEnabled ? (
                <AutoOn>ON</AutoOn>
              ) : (
                <AutoOff>OFF</AutoOff>
              )}
            </AutoPublishLabel>
            <Switch
              checked={!!schedule?.autoReplyEnabled}
              disabled={
                scheduleLoading || autoReplySaving || schedule == null
              }
              aria-label="AI 댓글 자동 대응"
              onChange={(next) => void handleAutoReplyToggle(next)}
            />
          </AutoPublishControl>
        </ToolLeft>
        <ToolRight>
          {!composing && (
            <Button
              type="button"
              $size="sm"
              disabled={busy}
              onClick={() => setComposing(true)}
            >
              <PlusIcon size={14} /> 새 글
            </Button>
          )}
          <ToolButton type="button" onClick={() => setShowCalendar((v) => !v)} aria-pressed={showCalendar} aria-expanded={showCalendar} aria-controls="board-calendar"><CalendarIcon size={16} /> 캘린더</ToolButton>
          <ToolButton type="button" onClick={cycleSort} title="계정 글과 삭제된 글 정렬 변경" aria-label={`글 정렬: ${SORT_LABEL[sortOrder]}. 클릭하여 변경`}>
            글 {SORT_LABEL[sortOrder]} <ChevronDownIcon size={14} />
          </ToolButton>
          <ToolButton type="button" onClick={() => setShowNotionImport(true)}>노션 가져오기</ToolButton>
          <ToolButton type="button" onClick={() => setShowExtras((v) => !v)} aria-pressed={showExtras}>댓글·삭제 글</ToolButton>
          <ToolButton
            type="button"
            onClick={() => {
              void fetchPosts();
              void fetchReservations();
              void fetchSchedule();
              void fetchCommentReplies();
              void fetchAccounts().catch(() => undefined);
              setPollCycle((cycle) => cycle + 1);
            }}
          >
            <RefreshIcon size={16} /> 새로고침
          </ToolButton>
        </ToolRight>
      </Toolbar>

      <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
        {selectedAccount.reconnectRequired || selectedAccount.tokenHealth === 'REFRESH_FAILED' ? <Alert tone="danger">계정 연결을 복구해야 합니다. <Link to="/account">계정 설정에서 다시 연결하세요.</Link></Alert> : selectedAccount.tokenHealth === 'EXPIRING' ? <Alert tone="warning">계정 연결 만료가 임박했습니다. 계정 설정에서 연결 상태를 확인하세요.</Alert> : null}
        {failedTaskCount > 0 && <Alert tone="warning">실패 {failedTaskCount}건 · 원인을 확인한 뒤 재시도하거나 기록을 보관할 수 있습니다.</Alert>}
        {stalledTaskCount > 0 && <Alert tone="warning">15분 이상 처리 중인 작업 {stalledTaskCount}건이 있습니다. 발행 결과를 확인한 뒤 복구하세요.</Alert>}
        {unknownTaskCount > 0 && <Alert tone="danger">발행 결과 확인 필요 {unknownTaskCount}건 · 중복 발행을 막기 위해 자동 재시도가 중지되었습니다.</Alert>}
        {awaitingCount > 0 && <Alert tone="info">승인 대기 {awaitingCount}건 · 초안을 열어 승인하면 예약 시각에 발송됩니다.</Alert>}
        {pollingStopped && <Alert tone="info">자동 상태 확인이 일시정지되었습니다. 새로고침하면 다시 시작합니다.</Alert>}
      </div>
      {showCalendar && <div id="board-calendar"><ReservationCalendar tasks={tasks} posts={donePosts} onEdit={setDraftModalTask} /></div>}
      {showNotionImport && <NotionImportModal accountId={selectedAccountId} accountLabel={accountLabel}
        autoPublishEnabled={!!schedule?.notionEnabled} reviewRequired={!!schedule?.reviewRequired}
        onClose={() => setShowNotionImport(false)}
        onImported={() => { void fetchReservations(); setPollCycle((cycle) => cycle + 1); }} />}
      {archivedTasks.length > 0 && <details style={{ marginBottom: 16 }}><summary>보관한 실패 기록 {archivedTasks.length}건</summary>{archivedTasks.map((task) => <div key={task.id} style={{ padding: 10, fontSize: 13 }}><strong>#{task.id} {task.subject}</strong><p>{task.errorMessage}</p></div>)}</details>}
      {legacyTasks.length > 0 && <LegacyHistory>
        <summary>이전 작업 기록 {legacyTasks.length}건</summary>
        {legacyTasks.map((task) => <article key={task.id}>
          <strong>#{task.id} {task.subject}</strong>
          {task.createdAt && <Meta>{formatLocalDateTime(task.createdAt)}</Meta>}
          <p>{task.draftPost || '저장된 본문이 없습니다.'}</p>
          {task.publishedPermalink && <a href={task.publishedPermalink} target="_blank" rel="noreferrer">게시글 보기 ↗</a>}
        </article>)}
      </LegacyHistory>}
      <Board $view="board">
        <Column>
          <ColumnHead $color="todo">
            <ColTitleRow>
              <ColDot $color="todo" />
              <ColTitle>예약 작업</ColTitle>
              <Count>{visibleTasks.length}</Count>
              <ColHeadActions>
                {failedTaskCount > 0 && (
                  <ClearFailedBtn
                    type="button"
                    disabled={clearingFailed}
                    onClick={() => void handleRemoveFailedTasks()}
                  >
                    {clearingFailed ? <Spinner $size={14} /> : '실패 기록 보관'}
                  </ClearFailedBtn>
                )}
                <AddColBtn
                  type="button"
                  disabled={busy}
                  onClick={() => setComposing((v) => !v)}
                  aria-label="작업 추가"
                >
                  <PlusIcon size={15} />
                </AddColBtn>
              </ColHeadActions>
            </ColTitleRow>
          </ColumnHead>

          <ColumnSearch>
            <SearchIcon size={15} />
            <input
              value={tasksQuery}
              onChange={(e) => setTasksQuery(e.target.value)}
              placeholder="예약 검색 (주제·초안)"
              aria-label="예약 작업 검색"
            />
          </ColumnSearch>



          {composing && (
            <Compose onSubmit={handleAdd}>
              <ComposeLabel htmlFor="new-post-subject">어떤 주제로 글을 만들까요?</ComposeLabel>
              <Input
                id="new-post-subject"
                autoFocus
                disabled={busy}
                placeholder="예: 처음 시작하는 사람을 위한 팁 3가지"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
              {products.length > 0 && (
                <ModeSelect
                  disabled={busy}
                  value={productId === '' ? '' : String(productId)}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) {
                      setProductId('');
                      setPublishMode('PERSONA');
                    } else {
                      setProductId(Number(v));
                      setPublishMode('PRODUCT');
                    }
                  }}
                  aria-label="소재 선택"
                >
                  <option value="">페르소나로 쓰기</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      소재: {p.name}
                    </option>
                  ))}
                </ModeSelect>
              )}
              <ComposeActions>
                <Button type="button" $variant="ghost" $size="sm" disabled={busy} onClick={() => setComposing(false)}>취소</Button>
                <Button type="submit" $size="sm" disabled={busy || !subject.trim()}>
                  {busy ? <><Spinner $size={14} /> 초안 준비 중…</> : '예약 작업 추가'}
                </Button>
              </ComposeActions>
            </Compose>
          )}

          <CardStack>
            {tasksError && <Alert tone="danger">{tasksError}</Alert>}
            {tasksLoading ? (
              <ColLoading>
                <Spinner $size={22} />
              </ColLoading>
            ) : visibleTasks.length === 0 && !composing ? (
              tasksQuery.trim() ? (
                <ColEmpty as="div" $static>
                  검색 결과가 없습니다.
                </ColEmpty>
              ) : (
                <ColEmpty type="button" onClick={() => setComposing(true)}>
                  + 새 예약 작업 추가
                  <br />
                  <EmptyHelp>주제를 입력해 첫 초안을 준비하세요. 자동 발행을 켜면 예약 작업도 함께 표시됩니다.</EmptyHelp>
                </ColEmpty>
              )
            ) : (
              <>
                {pagedTasks.map((task) => (
                <TaskCard
                  key={task.id}
                  $clickable={editableReservation(task)}
                  onClick={() => {
                    if (editableReservation(task)) setDraftModalTask(task);
                  }}
                >
                  <CardTopRow>
                    <Pill
                      $tone={
                        task.status === 'FAILED'
                          ? 'high'
                          : editableReservation(task)
                            ? 'progress'
                            : task.status === 'PROCESSING'
                              ? 'progress'
                              : 'low'
                      }
                    >
                      {task.status === 'PROCESSING' ? (
                        <>
                          <Spinner $size={11} /> 제작 중
                        </>
                      ) : (
                        reservationStatusLabel(task.status)
                      )}
                    </Pill>
                    <IconBtn
                      type="button"
                      title="삭제"
                      aria-label="예약 삭제"
                      disabled={task.status === 'PUBLISHING' || task.status === 'PROCESSING'}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        void handleRemoveTask(task);
                      }}
                    >
                      <TrashIcon size={15} />
                    </IconBtn>
                  </CardTopRow>
                  <CardTitle>{task.subject}</CardTitle>
                  {task.notionPageId && <a href={'https://www.notion.so/' + task.notionPageId.replace(/-/g, '')}
                    target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>노션 원문 ↗</a>}
                  <Meta style={{ marginTop: 4 }}><Pill $tone="low">{reservationKind(task)}</Pill></Meta>
                  {editableReservation(task) && task.draftPost && (
                    <DraftPreview>{task.draftPost}</DraftPreview>
                  )}
                  {editableReservation(task) && task.draftImageUrl && (
                    <DraftThumb src={task.draftImageUrl} alt="" />
                  )}
                  {(task.status === 'FAILED' || task.status === 'PUBLISH_UNKNOWN') && task.errorMessage && (
                    <ErrorText>{task.errorMessage}</ErrorText>
                  )}
                  {task.status === 'PUBLISH_UNKNOWN' && <OptionalReconciliation>
                    <summary>게시된 글 연결 (선택)</summary>
                    <ReservationReconcileForm task={task} onReconciled={() => { void fetchReservations(); void fetchPosts(false); }} />
                  </OptionalReconciliation>}
                  <CardFooter>
                    <Meta>
                      <CalendarIcon size={15} />
                      {task.scheduledAt
                        ? `${formatLocalDateTime(task.scheduledAt)} 발송`
                        : editableReservation(task)
                          ? '시각 미지정 · 지금 발송 가능'
                          : task.createdAt
                            ? formatWallClockDateKo(task.createdAt)
                            : '대기 중'}
                    </Meta>
                    {editableReservation(task) ? (
                      <GenerateLink
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDraftModalTask(task);
                        }}
                      >
                        초안 편집
                      </GenerateLink>
                    ) : task.status === 'PROCESSING' || task.status === 'PUBLISHING' ? (
                      <Meta>
                        {task.status === 'PUBLISHING' ? '발송 중' : preparingId === task.id ? '제작 중…' : '제작 중'}
                      </Meta>
                    ) : task.status === 'PUBLISH_UNKNOWN' ? (
                      <Meta>{task.publishedPermalink ? <a href={task.publishedPermalink} target="_blank" rel="noreferrer">Threads에서 결과 확인</a> : '발행 결과 확인 없이 예약을 삭제할 수 있습니다.'}</Meta>
                    ) : (
                      <GenerateLink
                        type="button"
                        disabled={busy}
                        onClick={(e) => {
                          e.stopPropagation();
                          task.status === 'FAILED'
                            ? void handleRetryFailed(task)
                            : void handlePrepare(task);
                        }}
                      >
                        {task.status === 'FAILED' ? '재시도' : '컨텐츠 제작'}
                      </GenerateLink>
                    )}
                  </CardFooter>
                </TaskCard>
              ))}
                <ColumnPager
                  page={Math.min(tasksPage, tasksTotalPages)}
                  totalPages={tasksTotalPages}
                  total={visibleTasks.length}
                  onChange={setTasksPage}
                />
              </>
            )}
          </CardStack>
        </Column>

        <Column>
          <ColumnHead $color="done">
            <ColTitleRow>
              <ColDot $color="done" />
              <ColTitle>계정 글</ColTitle>
              <Count>{registeredPosts.length}</Count>
            </ColTitleRow>
          </ColumnHead>

          <ColumnSearch>
            <SearchIcon size={15} />
            <input
              value={registeredQuery}
              onChange={(e) => setRegisteredQuery(e.target.value)}
              placeholder="글 검색 (본문·태그)"
              aria-label="계정 글 검색"
            />
          </ColumnSearch>

          {doneError && <Alert tone="danger">{doneError}</Alert>}

          <CardStack>
            {doneLoading ? (
              <ColLoading>
                <Spinner $size={22} />
              </ColLoading>
            ) : registeredPosts.length === 0 ? (
              <ColEmpty as="div" $static>
                {registeredQuery.trim()
                  ? '검색 결과가 없습니다.'
                  : '아직 글이 없습니다. 앱에서 발행하거나 Threads에 올린 글이 동기화되면 여기에 표시됩니다.'}
              </ColEmpty>
            ) : (
              <>
                {pagedRegisteredPosts.map((post) => {
                const isImported = post.threadContainerId === 'imported';
                const replyCount = commentReplies.filter(
                  (item) =>
                    item.threadAccountPostId === post.id ||
                    (!!post.threadPostId &&
                      item.rootThreadPostId === post.threadPostId)
                ).length;
                return (
                  <DoneCard
                    key={post.id}
                    $clickable
                    onClick={() => setSelectedPost(post)}
                    title="글·AI 댓글 보기"
                  >
                    <CardTopRow>
                      <Pill $tone={isImported ? 'progress' : 'done'}>
                        {isImported ? '연동' : '발행됨'}
                      </Pill>
                      <IconBtn
                        type="button"
                        title="삭제"
                        aria-label="게시물 삭제"
                        disabled={deletingId === post.id}
                        onClick={(e) => void handleDelete(post, e)}
                      >
                        {deletingId === post.id ? (
                          <Spinner $size={14} />
                        ) : (
                          <TrashIcon size={15} />
                        )}
                      </IconBtn>
                    </CardTopRow>
                    <DoneBody $clickable>{post.post}</DoneBody>
                    {post.insights && (
                      <InsightsRow onClick={(e) => e.stopPropagation()}>
                        <InsightItem title="조회수">
                          조회 {formatInsightCount(post.insights.views)}
                        </InsightItem>
                        <InsightItem title="좋아요">
                          좋아요 {formatInsightCount(post.insights.likes)}
                        </InsightItem>
                        <InsightItem title="댓글">
                          <CommentIcon size={14} />
                          {formatInsightCount(post.insights.replies)}
                        </InsightItem>
                        {(post.insights.reposts != null ||
                          post.insights.quotes != null) && (
                          <InsightItem title="리포스트·인용">
                            리포스트 {formatInsightCount(post.insights.reposts)} ·
                            인용 {formatInsightCount(post.insights.quotes)}
                          </InsightItem>
                        )}
                      </InsightsRow>
                    )}
                    <CardFooter>
                      <Avatar
                        src={profile?.threadProfilePictureUrl}
                        name={profile?.threadUserName}
                        size={24}
                      />
                      <MetaGroup>
                        {post.category && post.category !== '연동' && (
                          <Meta>
                            <TagIcon size={15} />#{post.category}
                          </Meta>
                        )}
                        <Meta>
                          <CalendarIcon size={15} />
                          {post.registeredAt
                            ? formatWallClockDateKo(post.registeredAt)
                            : `ID ${post.id}`}
                        </Meta>
                        <OpenHint type="button" aria-label={`게시물 ${post.id} 글과 AI 댓글 보기`} onClick={(event) => { event.stopPropagation(); setSelectedPost(post); }}>
                          {replyCount > 0
                            ? `AI 댓글 ${replyCount}`
                            : '자세히 보기'}
                        </OpenHint>
                      </MetaGroup>
                    </CardFooter>
                  </DoneCard>
                );
              })}
                <ColumnPager
                  page={Math.min(registeredPage, registeredTotalPages)}
                  totalPages={registeredTotalPages}
                  total={registeredPosts.length}
                  onChange={setRegisteredPage}
                />
              </>
            )}
          </CardStack>
        </Column>

        {showExtras && (
        <Column>
          <ColumnHead $color="muted">
            <ColTitleRow>
              <ColDot $color="muted" />
              <ColTitle>삭제된 글</ColTitle>
              <Count>{deletedPosts.length}</Count>
            </ColTitleRow>
          </ColumnHead>

          <ColumnSearch>
            <SearchIcon size={15} />
            <input
              value={deletedQuery}
              onChange={(e) => setDeletedQuery(e.target.value)}
              placeholder="삭제 글 검색 (본문·태그)"
              aria-label="삭제된 글 검색"
            />
          </ColumnSearch>

          <CardStack>
            {doneLoading ? (
              <ColLoading>
                <Spinner $size={22} />
              </ColLoading>
            ) : deletedPosts.length === 0 ? (
              <ColEmpty as="div" $static>
                {deletedQuery.trim()
                  ? '검색 결과가 없습니다.'
                  : '삭제된 글이 없습니다.'}
              </ColEmpty>
            ) : (
              <>
                {pagedDeletedPosts.map((post) => (
                <DoneCard key={post.id}>
                  <CardTopRow>
                    <Pill $tone="low">삭제됨</Pill>
                  </CardTopRow>
                  <DoneBody>{post.post}</DoneBody>
                  <CardFooter>
                    <MetaGroup>
                      {post.category && (
                        <Meta>
                          <TagIcon size={15} />#{post.category}
                        </Meta>
                      )}
                      <Meta>
                        <CalendarIcon size={15} />
                        {post.unRegisteredAt
                          ? formatWallClockDateKo(post.unRegisteredAt)
                          : `ID ${post.id}`}
                      </Meta>
                    </MetaGroup>
                    <GenerateLink
                      type="button"
                      disabled={copyingId === post.id || busy}
                      onClick={(e) => void handleCopyPost(post, e)}
                    >
                      {copyingId === post.id ? (
                        <Spinner $size={14} />
                      ) : (
                        '본문 복사'
                      )}
                    </GenerateLink>
                  </CardFooter>
                </DoneCard>
              ))}
                <ColumnPager
                  page={Math.min(deletedPage, deletedTotalPages)}
                  totalPages={deletedTotalPages}
                  total={deletedPosts.length}
                  onChange={setDeletedPage}
                />
              </>
            )}
          </CardStack>
        </Column>

)}
        {showExtras && (
        <Column>
          <ColumnHead $color="progress">
            <ColTitleRow>
              <ColDot $color="progress" />
              <ColTitle>댓글 대응</ColTitle>
              <Count>{visibleComments.length}</Count>
            </ColTitleRow>
          </ColumnHead>

          <ColumnSearch>
            <SearchIcon size={15} />
            <input
              value={commentsQuery}
              onChange={(e) => setCommentsQuery(e.target.value)}
              placeholder="댓글·답글 검색"
              aria-label="댓글 대응 검색"
            />
          </ColumnSearch>

          {commentsError && <Alert tone="danger">{commentsError}</Alert>}
          <CardStack>
            {commentsLoading ? (
              <ColLoading>
                <Spinner $size={22} />
              </ColLoading>
            ) : visibleComments.length === 0 ? (
              <ColEmpty as="div" $static>
                {commentsQuery.trim()
                  ? '검색 결과가 없습니다.'
                  : schedule?.autoReplyEnabled
                    ? '아직 처리한 댓글이 없습니다. 민감한 댓글은 검토 대상으로 남습니다.'
                    : '상단 「AI 댓글」을 켜면 댓글을 분류하고 답글을 준비합니다.'}
              </ColEmpty>
            ) : (
              <>
                {pagedComments.map((item) => (
                  <DoneCard key={item.id}>
                    <CardTopRow>
                      <Pill
                        $tone={
                          item.status === 'REPLIED'
                            ? 'done'
                            : item.status === 'SCHEDULED'
                              ? 'progress'
                            : item.status === 'PINNED'
                              ? 'done'
                              : item.status === 'POST_HIDDEN'
                              ? 'high'
                              : item.status === 'HIDDEN'
                                ? 'progress'
                                : item.status === 'FAILED'
                                  ? 'high'
                                  : 'low'
                        }
                      >
                        {item.status === 'REVIEW_REQUIRED' ? '검토 필요' : item.status === 'CANCELLED' ? '취소됨' : item.status === 'REPLIED'
                          ? '답글 완료'
                          : item.status === 'SCHEDULED'
                            ? '답글 예약'
                          : item.status === 'PINNED'
                            ? '댓글 고정'
                            : item.status === 'POST_HIDDEN'
                            ? '원글 숨김'
                            : item.status === 'HIDDEN'
                              ? '댓글 숨김'
                              : item.status === 'IGNORED'
                                ? '무시'
                                : '실패'}
                      </Pill>
                    </CardTopRow>
                    <Meta>
                      <CommentIcon size={14} />
                      {item.commentUsername
                        ? `@${item.commentUsername}`
                        : '댓글'}
                    </Meta>
                    <DoneBody>{item.commentText || '(본문 없음)'}</DoneBody>
                    {item.replyText && (
                      <DraftPreview>↳ {item.replyText}</DraftPreview>
                    )}
                    {item.decisionReason && (
                      <Meta>{item.decisionReason}</Meta>
                    )}
                    {item.errorMessage && (
                      <ErrorText>{item.errorMessage}</ErrorText>
                    )}
                  </DoneCard>
                ))}
                <ColumnPager
                  page={Math.min(commentsPage, commentsTotalPages)}
                  totalPages={commentsTotalPages}
                  total={visibleComments.length}
                  onChange={setCommentsPage}
                />
              </>
            )}
          </CardStack>
        </Column>
        )}
      </Board>

      <ReservationDraftModal
        open={draftModalTask != null}
        task={draftModalTask}
        onClose={() => setDraftModalTask(null)}
        onSaved={(updated) => {
          setTasks((prev) =>
            prev.map((t) => (t.id === updated.id ? updated : t))
          );
          setDraftModalTask(updated);
        }}
        onPublished={() => {
          setDraftModalTask(null);
          void fetchPosts();
          void fetchReservations();
        }}
      />

      <Modal
        open={selectedPost != null}
        onClose={() => setSelectedPost(null)}
        width={560}
        labelledBy="post-detail-title"
      >
        {selectedPost && (
          <PostDetail>
            <PostDetailHead>
              <div>
                <PostDetailTitle id="post-detail-title">계정 글</PostDetailTitle>
                <PostDetailMeta>
                  {selectedPost.registeredAt
                    ? formatWallClockKo(selectedPost.registeredAt)
                    : `ID ${selectedPost.id}`}
                </PostDetailMeta>
              </div>
              <PostDetailActions>
                {selectedPost.threadPostUrl && (
                  <Button
                    type="button"
                    $size="sm"
                    onClick={() => handleOpenThreadsPost(selectedPost)}
                  >
                    Threads에서 보기
                  </Button>
                )}
                <Button
                  type="button"
                  $variant="ghost"
                  $size="sm"
                  onClick={() => setSelectedPost(null)}
                >
                  닫기
                </Button>
              </PostDetailActions>
            </PostDetailHead>

            <PostDetailBody>{selectedPost.post}</PostDetailBody>

            <CommentSection>
              <CommentSectionTitle>
                <CommentIcon size={16} />
                AI 댓글 대응
                <Count>{postCommentReplies.length}</Count>
              </CommentSectionTitle>

              {commentsLoading ? (
                <ColLoading>
                  <Spinner $size={20} />
                </ColLoading>
              ) : postCommentReplies.length === 0 ? (
                <CommentEmpty>
                  이 글에 기록된 AI 댓글 대응이 없습니다.
                  {schedule?.autoReplyEnabled
                    ? ' 새 댓글이 오면 자동으로 처리됩니다.'
                    : ' 보드 상단 「AI 댓글」을 켜면 여기에 쌓입니다.'}
                </CommentEmpty>
              ) : (
                <CommentList>
                  {postCommentReplies.map((item) => (
                    <CommentItem key={item.id}>
                      <CardTopRow>
                        <Pill
                          $tone={
                            item.status === 'REPLIED'
                              ? 'done'
                              : item.status === 'SCHEDULED'
                                ? 'progress'
                                : item.status === 'PINNED'
                                ? 'done'
                                : item.status === 'POST_HIDDEN'
                                ? 'high'
                                : item.status === 'HIDDEN'
                                  ? 'progress'
                                  : item.status === 'FAILED'
                                    ? 'high'
                                    : 'low'
                          }
                        >
                          {item.status === 'REVIEW_REQUIRED' ? '검토 필요' : item.status === 'CANCELLED' ? '취소됨' : item.status === 'REPLIED'
                            ? '답글 완료'
                            : item.status === 'SCHEDULED'
                              ? '답글 예약'
                              : item.status === 'PINNED'
                              ? '댓글 고정'
                              : item.status === 'POST_HIDDEN'
                              ? '원글 숨김'
                              : item.status === 'HIDDEN'
                                ? '댓글 숨김'
                                : item.status === 'IGNORED'
                                  ? '무시'
                                  : '실패'}
                        </Pill>
                      </CardTopRow>
                      <Meta>
                        <CommentIcon size={14} />
                        {item.commentUsername
                          ? `@${item.commentUsername}`
                          : '댓글'}
                        {item.commentTimestamp && (
                          <> · {formatWallClockKo(item.commentTimestamp)}</>
                        )}
                      </Meta>
                      <DoneBody>{item.commentText || '(본문 없음)'}</DoneBody>
                      {item.replyText && (
                        <AiReplyBlock>
                          <AiReplyLabel>AI 답글</AiReplyLabel>
                          {item.replyText}
                        </AiReplyBlock>
                      )}
                      {item.decisionReason && (
                        <Meta>{item.decisionReason}</Meta>
                      )}
                      {item.errorMessage && (
                        <ErrorText>{item.errorMessage}</ErrorText>
                      )}
                    </CommentItem>
                  ))}
                </CommentList>
              )}
            </CommentSection>
          </PostDetail>
        )}
      </Modal>
    </Page>
  );
}

function ColumnPager({
  page,
  totalPages,
  total,
  onChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  onChange: (page: number) => void;
}) {
  if (total <= BOARD_PAGE_SIZE) return null;
  const from = (page - 1) * BOARD_PAGE_SIZE + 1;
  const to = Math.min(page * BOARD_PAGE_SIZE, total);

  return (
    <Pager>
      <PagerInfo>
        {from}-{to} / {total}
      </PagerInfo>
      <PagerControls>
        <PagerBtn
          type="button"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
          aria-label="이전 페이지"
        >
          이전
        </PagerBtn>
        <PagerPage>
          {page} / {totalPages}
        </PagerPage>
        <PagerBtn
          type="button"
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
          aria-label="다음 페이지"
        >
          다음
        </PagerBtn>
      </PagerControls>
    </Pager>
  );
}

/* ── page shell ── */
const PageEyebrow = styled.p`
  margin: 0 0 6px;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`;

const PageDescription = styled.p`
  margin: 8px 0 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
  line-height: 1.7;
`;

const Overview = styled.dl`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1px;
  overflow: hidden;
  margin: 0 0 24px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.border};
  @media (max-width: 520px) { margin-bottom: 16px; }
`;

const OverviewItem = styled.div`
  padding: 18px 20px;
  background: ${({ theme }) => theme.colors.surface};
  dt { color: ${({ theme }) => theme.colors.textSecondary}; font-size: 13px; }
  dd { margin: 8px 0 0; font-size: 26px; font-weight: 700; line-height: 1.2; font-variant-numeric: tabular-nums; }
  span { margin-left: 5px; font-size: 13px; font-weight: 500; color: ${({ theme }) => theme.colors.textMuted}; }
  @media (max-width: 520px) {
    padding: 14px 8px;
    text-align: center;
    dt { font-size: 11px; white-space: nowrap; }
    dd { margin-top: 6px; font-size: 22px; }
    span { margin-left: 3px; font-size: 11px; }
  }
`;

const AccountLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 18px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};
  font-size: 14px;
  font-weight: 600;
`;

const Page = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

/* ── title / toolbar ── */
const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
  flex-wrap: wrap;
  flex-shrink: 0;
`;

const TitleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const SquareIcon = styled(Link)`
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primary};

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.onPrimary};
  }
`;

const TopRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  @media (max-width: 768px) { display: none; }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const AutomationToggle = styled.button`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-height: 44px;
    padding: 10px 12px;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.radius.md};
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.textPrimary};
    font-size: 13px;
    font-weight: 600;
    text-align: left;
    cursor: pointer;
    svg { flex-shrink: 0; transition: transform 0.15s ease; }
    &[aria-expanded='true'] svg { transform: rotate(180deg); }
  }
`;

const AutomationState = styled.span`
  margin-left: auto;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 12px;
  font-weight: 400;
`;

const AutomationSuffix = styled.span`
  @media (max-width: 768px) { display: none; }
`;

const ToolLeft = styled.div<{ $expanded: boolean }>`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    display: ${({ $expanded }) => $expanded ? 'grid' : 'none'};
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const ToolRight = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const AutoPublishControl = styled.div`
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 0 12px 0 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  @media (max-width: 768px) {
    justify-content: space-between;
    gap: 6px;
    padding: 0 10px;
  }
`;

const AutoPublishLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const AutoOn = styled.span`
  @media (max-width: 768px) { display: none; }
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.success};
`;

const AutoOff = styled.span`
  @media (max-width: 768px) { display: none; }
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const ToolButton = styled.button`
  justify-content: center;
  cursor: pointer;
  &[aria-pressed='true'] {
    color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primarySoft};
  }
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 0 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceMuted};
  }
`;

const ToolDivider = styled.span`
  @media (max-width: 768px) { display: none; }
  width: 1px;
  height: 24px;
  background: ${({ theme }) => theme.colors.border};
`;

/* ── board / columns ── */
const Board = styled.div<{ $view: 'board' | 'list' }>`
  width: 100%;
  display: grid;
  grid-template-columns: ${({ $view }) =>
    $view === 'board' ? 'repeat(2, minmax(0, 1fr))' : '1fr'};
  gap: 20px;
  align-items: start;

  @media (max-width: 1400px) {
    grid-template-columns: ${({ $view }) =>
      $view === 'board' ? 'repeat(2, minmax(0, 1fr))' : '1fr'};
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.section`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const columnColors = {
  todo: '#2563eb',
  progress: '#0ea5e9',
  done: '#059669',
  muted: '#94a3b8',
} as const;

const ColumnHead = styled.div<{ $color: keyof typeof columnColors }>`
  flex-shrink: 0;
  padding-bottom: 12px;
  border-bottom: 2px solid
    ${({ $color }: { $color: keyof typeof columnColors }) =>
      columnColors[$color]};
`;

const ColumnSearch = styled.label`
  min-height: 44px;
  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primarySoft};
  }
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textMuted};

  input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: none;
    background: transparent;
    font-size: 14px;
    @media (max-width: 768px) { font-size: 16px; }
    color: ${({ theme }) => theme.colors.textPrimary};

    &::placeholder {
      color: ${({ theme }) => theme.colors.textMuted};
    }
  }
`;

const ColTitleRow = styled.div`
  display: flex;
  min-height: 44px;
  align-items: center;
  gap: 8px;
`;

const ColDot = styled.span<{ $color: keyof typeof columnColors }>`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ $color }: { $color: keyof typeof columnColors }) =>
    columnColors[$color]};
`;

const ColTitle = styled.h2`
  font-size: 15px;
  font-weight: 700;
`;

const Count = styled.span`
  display: inline-grid;
  place-items: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ColHeadActions = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ClearFailedBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 0 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.danger};
  background: ${({ theme }) => theme.colors.dangerSoft};

  &:hover:not(:disabled) {
    filter: brightness(0.97);
  }

  &:disabled {
    opacity: 0.65;
    cursor: default;
  }
`;

const AddColBtn = styled.button`
  width: 44px;
  flex-shrink: 0;
  min-height: 44px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primary};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.onPrimary};
  }

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

const ComposeLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
`;

const ComposeActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
`;

const Compose = styled.form`
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.surface};
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
`;

const ModeSelect = styled.select`
  min-height: 44px;
  flex: 1;
  min-width: 120px;
  padding: 8px 10px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 13px;
`;

const CardStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 4px 2px 2px;
  margin-top: 2px;
`;

const PagerInfo = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
`;

const PagerControls = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PagerPage = styled.span`
  min-width: 48px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const PagerBtn = styled.button`
  min-height: 44px;
  padding: 0 10px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 12px;
  font-weight: 600;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.surfaceMuted};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const BaseCard = styled.div`
  min-width: 0;
  overflow-wrap: anywhere;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  box-shadow: ${({ theme }) => theme.shadow.sm};
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition:
    box-shadow 0.15s ease,
    transform 0.15s ease;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadow.md};
  }
`;

const TaskCard = styled(BaseCard)<{ $clickable?: boolean }>`
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

const DraftPreview = styled.p`
  margin: 0 0 8px;
  font-size: 14px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-wrap;
`;

const DraftThumb = styled.img`
  width: 100%;
  max-height: 120px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.sm};
  margin-bottom: 8px;
  background: ${({ theme }) => theme.colors.surfaceMuted};
`;

const DoneCard = styled(BaseCard)<{ $clickable?: boolean }>`
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  ${({ $clickable, theme }) =>
    $clickable &&
    `
    &:hover {
      border-color: ${theme.colors.primary};
    }
  `}
`;

const DoneBody = styled.p<{ $clickable?: boolean }>`
  font-size: 14px;
  line-height: 1.75;
  color: ${({ theme, $clickable }) =>
    $clickable ? theme.colors.textPrimary : theme.colors.textSecondary};
  white-space: pre-wrap;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;

  ${({ $clickable, theme }) =>
    $clickable &&
    `
    &:hover {
      color: ${theme.colors.primary};
    }
  `}
`;

const CardTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const pillTones = {
  low: { bg: 'surfaceMuted', fg: 'textSecondary' },
  high: { bg: 'dangerSoft', fg: 'danger' },
  progress: { bg: 'primarySoft', fg: 'primary' },
  done: { bg: 'successSoft', fg: 'success' },
} as const;

const Pill = styled.span<{ $tone: keyof typeof pillTones }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: 12px;
  font-weight: 600;
  background: ${({ theme, $tone }) => theme.colors[pillTones[$tone as keyof typeof pillTones].bg]};
  color: ${({ theme, $tone }) => theme.colors[pillTones[$tone as keyof typeof pillTones].fg]};
`;

const CardTitle = styled.p`
  font-size: 15px;
  font-weight: 700;
  line-height: 1.45;
`;

const CardFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  justify-content: space-between;
`;

const MetaGroup = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  margin-left: 2px;
`;

const Meta = styled.span`
  min-width: 0;
  line-height: 1.6;
  svg { flex-shrink: 0; }
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textMuted};

  svg {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const GenerateLink = styled.button`
  min-height: 44px;
  padding: 8px 4px;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  &:hover:not(:disabled) {
    text-decoration: underline;
  }
  &:disabled {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const IconBtn = styled.button`
  width: 44px;
  flex-shrink: 0;
  min-height: 44px;
  border-radius: 6px;
  display: grid;
  place-items: center;
  color: ${({ theme }) => theme.colors.textMuted};
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.surfaceMuted};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  &:disabled {
    opacity: 0.5;
  }
`;

const ErrorText = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.danger};
  line-height: 1.5;
`;

const LegacyHistory = styled.details`
  margin-bottom: 16px; font-size: 13px; color: ${({ theme }) => theme.colors.textSecondary};
  summary { cursor: pointer; padding: 12px 0; }
  article { display: grid; gap: 8px; padding: 16px 0; border-top: 1px solid ${({ theme }) => theme.colors.border}; }
  strong { color: ${({ theme }) => theme.colors.textPrimary}; overflow-wrap: anywhere; }
  p { margin: 0; white-space: pre-wrap; overflow-wrap: anywhere; line-height: 1.7; }
  a { color: ${({ theme }) => theme.colors.primary}; }
`;

const OptionalReconciliation = styled.details`
  min-width: 0;
  summary {
    padding: 12px 0;
    min-height: 44px;
    cursor: pointer;
    font-size: 13px;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const OpenHint = styled.button`
  min-height: 44px;
  padding: 8px 4px;
  flex-shrink: 0;
  &:hover { text-decoration: underline; }
  margin-left: auto;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
`;

const InsightsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surfaceMuted};
`;

const InsightItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const EmptyHelp = styled.span`
  display: block;
  max-width: 360px;
  margin: 8px auto 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: 400;
`;

const ColEmpty = styled.button<{ $static?: boolean }>`
  padding: 30px 20px;
  line-height: 1.7;
  text-align: center;
  font-size: 13px;
  width: 100%;
  color: ${({ theme }) => theme.colors.textMuted};
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.lg};
  cursor: ${({ $static }) => ($static ? 'default' : 'pointer')};

  &:hover {
    background: ${({ $static, theme }) =>
      $static ? 'transparent' : theme.colors.surfaceMuted};
  }
`;

const ColLoading = styled.div`
  display: flex;
  justify-content: center;
  padding: 30px;
  color: ${({ theme }) => theme.colors.primary};
`;

const NoAccount = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 40px 20px;
  text-align: center;

  h2 {
    font-size: 16px;
    font-weight: 700;
  }
  p {
    font-size: 14px;
    color: ${({ theme }) => theme.colors.textSecondary};
    max-width: 380px;
  }
`;

const PostDetail = styled.div`
  padding: 24px;
  @media (max-width: 520px) { padding: 20px 16px; }
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const PostDetailHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const PostDetailTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
`;

const PostDetailMeta = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const PostDetailActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const PostDetailBody = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${({ theme }) => theme.colors.textPrimary};
  padding: 14px 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const CommentSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CommentSectionTitle = styled.h3`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const CommentEmpty = styled.p`
  margin: 0;
  padding: 16px;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textMuted};
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const CommentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: min(48vh, 420px);
  overflow-y: auto;
`;

const CommentItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
`;

const AiReplyBlock = styled.div`
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
`;

const AiReplyLabel = styled.span`
  display: block;
  margin-bottom: 4px;
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.primary};
`;
